import React, { useState, useMemo } from 'react';
import CRMHeader from '../components/CRM/CRMHeader';
import CRMKanbanBoard from '../components/CRM/CRMKanbanBoard';
import CRMListView from '../components/CRM/CRMListView';
import { useCRM } from '../context/CRMContext';
import T from '../components/common/T';
import Price from '../components/common/Price';
import { PageHeader, StatsGrid, TableCard } from '../components/common/PageLayout';
import { TopPrimaryButton } from '../components/common/StandardButtons';
import {
    UsersIcon, ChartBarIcon, HandThumbUpIcon,
    ArchiveBoxXMarkIcon,
} from '@heroicons/react/24/outline';

const CRM = () => {
    const { columns, setIsQuickAddVisible, fetchPipeline } = useCRM();
    const [viewMode, setViewMode] = useState('kanban');

    React.useEffect(() => {
        if (fetchPipeline) fetchPipeline();
    }, [fetchPipeline]);

    const stats = useMemo(() => {
        let totalLeads = 0, pipelineValue = 0, wonLeads = 0, lostLeads = 0;
        if (columns) {
            Object.values(columns).forEach(col => {
                col.items?.forEach(item => {
                    totalLeads++;
                    if (['Sold', 'Won', 'Booked'].includes(col.title)) wonLeads++;
                    else if (col.title === 'Lost') lostLeads++;
                    else pipelineValue += Number(item.budget) || 0;
                });
            });
        }
        return { totalLeads, pipelineValue, wonLeads, lostLeads };
    }, [columns]);

    const statsList = useMemo(() => [
        { label: "Total Leads", value: stats.totalLeads, icon: UsersIcon, variant: "default" },
        { label: "Pipeline Value", value: <Price amount={stats.pipelineValue} />, icon: ChartBarIcon, variant: "purple" },
        { label: "Deals Won", value: stats.wonLeads, icon: HandThumbUpIcon, variant: "green" },
        { label: "Deals Lost", value: stats.lostLeads, icon: ArchiveBoxXMarkIcon, variant: "red" },
    ], [stats]);

    return (
        <div className="p-2.5 sm:p-6 bg-gray-50 dark:bg-[#0d1117] min-h-screen flex flex-col font-sans transition-colors duration-150">

            {/* ── TOP SECTION — Header, KPIs + toolbar ── */}
            <div className="shrink-0">
                <PageHeader
                    icon={UsersIcon}
                    iconColor="text-[#E8612C]"
                    title="Sales Pipeline"
                    subtitle="Track leads, opportunities, and deals"
                    onRefresh={() => fetchPipeline && fetchPipeline()}
                >
                    <TopPrimaryButton
                        onClick={() => setIsQuickAddVisible(true)}
                        label="New Lead"
                    />
                </PageHeader>

                <StatsGrid stats={statsList} cols={4} />

                <CRMHeader viewMode={viewMode} setViewMode={setViewMode} />
            </div>

            {/* ── CONTENT SECTION — Kanban or List View ── */}
            <div className="flex-1 min-h-0">
                {viewMode === 'kanban' ? (
                    <div className="overflow-x-auto pb-4">
                        <CRMKanbanBoard />
                    </div>
                ) : viewMode === 'list' ? (
                    <TableCard>
                        <CRMListView />
                    </TableCard>
                ) : (
                    <TableCard className="flex items-center justify-center text-sm text-gray-400 dark:text-[#6e7681] min-h-[300px]">
                        Calendar view coming soon
                    </TableCard>
                )}
            </div>
        </div>
    );
};

export default CRM;